/**
 * Ministries picker component.
 * Multi-select dropdown for selecting ministries.
 * Used for RESTRICTED visibility and organizer selection.
 */

import { useState, useMemo } from "react";
import {
  IconChevronDown,
  IconCheck,
  IconX,
  IconSearch,
} from "../../../../components/Icons.tsx";
import { haptic } from "../../../../lib/telegram.ts";
import { useMinistries } from "../hooks";

interface MinistriesPickerProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  /** If true, allows selecting only one ministry */
  single?: boolean;
}

export function MinistriesPicker({
  selectedIds,
  onChange,
  placeholder = "Выберите служения...",
  single = false,
}: MinistriesPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // URQL hook - load ministries when dropdown opens
  const { ministries, isLoading, error: ministriesError } = useMinistries({
    pause: !isOpen,
  });
  const error = ministriesError?.message ?? null;

  // Filter ministries by search
  const filteredMinistries = useMemo(() => {
    return ministries.filter((m) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        m.name.toLowerCase().includes(query) ||
        m.code.toLowerCase().includes(query) ||
        m.description?.toLowerCase().includes(query)
      );
    });
  }, [ministries, searchQuery]);

  // Get selected ministries
  const selectedMinistries = useMemo(() => {
    return ministries.filter((m) => selectedIds.includes(m.id));
  }, [ministries, selectedIds]);

  // Toggle selection
  const toggleMinistry = (ministryId: string) => {
    haptic("light");
    if (single) {
      onChange([ministryId]);
      setIsOpen(false);
    } else {
      if (selectedIds.includes(ministryId)) {
        onChange(selectedIds.filter((id) => id !== ministryId));
      } else {
        onChange([...selectedIds, ministryId]);
      }
    }
  };

  // Remove selected ministry
  const removeMinistry = (ministryId: string) => {
    haptic("light");
    onChange(selectedIds.filter((id) => id !== ministryId));
  };

  return (
    <div className="relative">
      {/* Selected ministries chips */}
      {selectedMinistries.length > 0 && !single && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedMinistries.map((ministry) => (
            <span
              key={ministry.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-lg text-[13px] text-slate-700"
            >
              {ministry.name}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeMinistry(ministry.id);
                }}
                className="p-0.5 hover:bg-slate-200 rounded transition-colors"
              >
                <IconX className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={() => {
          haptic("light");
          setIsOpen(!isOpen);
        }}
        className="w-full flex items-center justify-between px-3 py-2.5 border border-slate-200 rounded-xl bg-white text-left active:bg-slate-50 transition-colors"
      >
        <span className="text-[15px] text-slate-900 truncate">
          {single && selectedMinistries.length > 0
            ? selectedMinistries[0].name
            : selectedIds.length > 0
              ? `Выбрано: ${selectedIds.length}`
              : placeholder}
        </span>
        <IconChevronDown
          className={`w-5 h-5 text-slate-400 transition-transform flex-shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 right-0 mt-1 z-20 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Поиск..."
                  className="w-full pl-9 pr-3 py-2 text-[14px] bg-slate-50 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* List */}
            <div className="max-h-[250px] overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  Загрузка...
                </div>
              ) : error ? (
                <div className="p-4 text-center text-red-500 text-sm">
                  {error}
                </div>
              ) : filteredMinistries.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  {searchQuery ? "Ничего не найдено" : "Нет служений"}
                </div>
              ) : (
                filteredMinistries.map((ministry) => {
                  const isSelected = selectedIds.includes(ministry.id);
                  return (
                    <button
                      key={ministry.id}
                      type="button"
                      onClick={() => toggleMinistry(ministry.id)}
                      className={`w-full px-3 py-2.5 text-left flex items-center gap-3 transition-colors ${
                        isSelected ? "bg-slate-50" : "hover:bg-slate-50"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? "bg-[var(--color-accent)] border-[var(--color-accent)]"
                            : "border-slate-300"
                        }`}
                      >
                        {isSelected && (
                          <IconCheck className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[15px] text-slate-900 block truncate">
                          {ministry.name}
                        </span>
                        {ministry.description && (
                          <span className="text-[12px] text-slate-500 block truncate">
                            {ministry.description}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
